import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useMerchantId } from '@/hooks/useMerchantId';

/**
 * FR-65: ported off `manage-data` (`profiles`+`user_roles`) onto `GET /staff`.
 * `UserResource.roles` is an array (a user can hold >1 role in the schema,
 * even though invite only ever grants one) — `role` here keeps the old
 * prototype's singular shape (highest-priority role) so existing consumers
 * (`Users.tsx`/`UsersTab.tsx`/`ApprovalTab.tsx`/`UserApproval.tsx`) that
 * filter/display a single role don't all need a parallel rewrite in the same
 * change; `full_name` mirrors the prototype's `profiles.full_name` column
 * (the resource itself calls it `name`).
 */
export function useProfiles() {
  const { merchantId } = useMerchantId();
  return useQuery({
    queryKey: ['profiles', merchantId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/staff');
      if (error) throw error;
      const rolePriority = ['owner', 'manager', 'cashier', 'viewer'];
      return (data?.data ?? []).map(p => ({
        ...p,
        full_name: p.name,
        role: [...(p.roles ?? [])].sort((a, b) => rolePriority.indexOf(a) - rolePriority.indexOf(b))[0] ?? 'viewer',
      }));
    },
    enabled: !!merchantId,
  });
}
